import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateTransportInput,
  ItemResponse,
  ListResponse,
  SetTransportStatusInput,
  TransportListItem,
  TransportResponse,
  UpdateTransportInput,
} from '@proerp/shared';
import { apiRequest } from '@/lib/apiClient';

/**
 * Datenzugriff des Minimalmoduls Transport (ADR 0079): auflisten, lesen, anlegen,
 * ändern, Status setzen, löschen. Nichts weiter.
 *
 * Die Liste holt bewusst ALLE Transporte (nur `q` geht an den Server) — Status,
 * Richtung und Sortierung filtert/sortiert `TransportsPage` selbst. So zeigen die
 * Filter-Chips die Anzahl jeder Auswahl, nicht nur der gerade aktiven (Muster
 * `ExpectedDeliveriesTab`). Bei bis zu 100 Zeilen (Minimalmodul) unproblematisch.
 */
const KEY = ['transports'] as const;

/**
 * `openOnly`/`plannedUntil` sind ausschließlich für das Dashboard-Widget da
 * (VB-26004): dort geht es um „was steht heute an", nicht um Chip-Zählungen — die
 * Übersichtsseite lässt beide weg und filtert weiter selbst.
 */
export function useTransports(
  params: { q?: string; openOnly?: boolean; plannedUntil?: string } = {},
) {
  const search = new URLSearchParams({ page: '1', pageSize: '100' });
  if (params.q) search.set('q', params.q);
  if (params.openOnly) search.set('open', 'true');
  if (params.plannedUntil) search.set('plannedUntil', params.plannedUntil);
  return useQuery({
    queryKey: [...KEY, 'list', params],
    queryFn: () => apiRequest<ListResponse<TransportListItem>>(`/v1/transports?${search.toString()}`),
    placeholderData: keepPreviousData,
  });
}

export function useTransport(id: string | undefined) {
  return useQuery({
    queryKey: [...KEY, 'detail', id],
    queryFn: () => apiRequest<ItemResponse<TransportResponse>>(`/v1/transports/${id}`),
    enabled: Boolean(id),
  });
}

/**
 * Nach jeder Schreib-Operation Detail UND Liste neu holen. Zusätzlich die
 * Wareneingänge: dort steht „angeliefert mit" — die Anzeige hängt am Transport.
 */
function useInvalidate(id?: string) {
  const queryClient = useQueryClient();
  return () => {
    if (id) queryClient.invalidateQueries({ queryKey: [...KEY, 'detail', id] });
    queryClient.invalidateQueries({ queryKey: KEY });
    queryClient.invalidateQueries({ queryKey: ['goods-receipts'] });
  };
}

export function useCreateTransport() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: CreateTransportInput) =>
      apiRequest<ItemResponse<TransportResponse>>('/v1/transports', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateTransport(id: string) {
  const invalidate = useInvalidate(id);
  return useMutation({
    mutationFn: (input: UpdateTransportInput) =>
      apiRequest<ItemResponse<TransportResponse>>(`/v1/transports/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    onSuccess: () => invalidate(),
  });
}

export function useSetTransportStatus(id: string) {
  const invalidate = useInvalidate(id);
  return useMutation({
    mutationFn: (input: SetTransportStatusInput) =>
      apiRequest<ItemResponse<TransportResponse>>(`/v1/transports/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    onSuccess: () => invalidate(),
  });
}

export function useDeleteTransport() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<{ data: { ok: true } }>(`/v1/transports/${id}`, { method: 'DELETE' }),
    onSuccess: () => invalidate(),
  });
}
